/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { TRANSLATIONS, LanguageCode } from '../translations';
import { 
  MessageSquare, 
  Send, 
  X, 
  Sparkles, 
  Trophy, 
  HelpCircle, 
  Lightbulb, 
  ChevronDown, 
  Brain, 
  Trash2,
  AlertCircle
} from 'lucide-react';

interface ChatMessage {
  id: string;
  role: 'user' | 'model';
  text: string;
  timestamp: string;
}

const PRESET_CHIPS = [
  { label: "📍 Match Prediction Rules", text: "Explain the Match Prediction rules and point scoring system." },
  { label: "⚽ How to win free Sticker Packs?", text: "What is the best way to earn free sticker packs in Paganini?" },
  { label: "💎 Rarity & Rare Cards Guide", text: "What are the different sticker rarities (standard, rare, legendary) and which players are Legendary?" },
  { label: "🤝 Trading Tips & Strategy", text: "How does the trading system work and what is a good swap strategy?" }
];

// Helper to manually parse simplified Markdown into visually polished HTML elements
function FormatBotResponse({ text }: { text: string }) {
  if (!text) return null;

  // Split into lines
  const lines = text.split('\n');

  return (
    <div className="space-y-2 text-slate-100 selection:bg-blue-500/30">
      {lines.map((line, idx) => {
        let clean = line.trim();
        if (!clean) return <div key={idx} className="h-1" />;

        // Header Check: e.g. "### Heading" or "## Heading" or "Heading:"
        if (clean.startsWith('###') || clean.startsWith('##') || clean.startsWith('#')) {
          const depthClass = clean.startsWith('###') ? 'text-[11px] font-black tracking-wide text-blue-400 mt-2 uppercase' : 'text-xs font-extrabold text-white mt-3 uppercase border-b border-slate-800/60 pb-0.5';
          const headerText = clean.replace(/^#+\s*/, '');
          return <h4 key={idx} className={`${depthClass} font-mono`}>{headerText}</h4>;
        }

        // List item Check
        const isBullet = clean.startsWith('-') || clean.startsWith('*');
        if (isBullet) {
          clean = clean.substring(1).trim();
        }

        // Parse bold text **something**
        const boldRegex = /\*\*(.*?)\*\*/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = boldRegex.exec(clean)) !== null) {
          if (match.index > lastIndex) {
            parts.push({ text: clean.substring(lastIndex, match.index), bold: false });
          }
          parts.push({ text: match[1], bold: true });
          lastIndex = boldRegex.lastIndex;
        }
        if (lastIndex < clean.length) {
          parts.push({ text: clean.substring(lastIndex), bold: false });
        }

        const content = parts.length > 0 ? (
          parts.map((p, pIdx) => p.bold ? (
            <strong key={pIdx} className="text-amber-400 font-extrabold">{p.text}</strong>
          ) : (
            <span key={pIdx} className="text-slate-200">{p.text}</span>
          ))
        ) : (
          <span className="text-slate-200">{clean}</span>
        );

        if (isBullet) {
          return (
            <div key={idx} className="flex items-start gap-1.5 pl-2">
              <span className="text-blue-400 text-[10px] mt-1 shrink-0">■</span>
              <span className="text-xs leading-relaxed">{content}</span>
            </div>
          );
        }

        return (
          <p key={idx} className="text-xs leading-relaxed">
            {content}
          </p>
        );
      })}
    </div>
  );
}

export function AIAgentBot() {
  const getLang = (): LanguageCode => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('paganini-lang');
      if (saved && saved in TRANSLATIONS) return saved as LanguageCode;
    }
    return 'en';
  };
  const lang = getLang();
  const t = (key: string) => TRANSLATIONS[lang]?.[key] || TRANSLATIONS['en']?.[key] || key;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load chat history from localStorage on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem('paganini_ai_chat');
      if (cached) {
        setMessages(JSON.parse(cached));
      } else {
        // Setup initial greeting message
        const welcome: ChatMessage = {
          id: 'welcome',
          role: 'model',
          text: t('aiBotInitialWelcome'),
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setMessages([welcome]);
        localStorage.setItem('paganini_ai_chat', JSON.stringify([welcome]));
      }
    } catch (e) {
      console.error("Local storage chat initialization error:", e);
    }
  }, [lang]);

  // Save chat history to localStorage
  const saveHistory = (newMessages: ChatMessage[]) => {
    setMessages(newMessages);
    try {
      localStorage.setItem('paganini_ai_chat', JSON.stringify(newMessages));
    } catch (e) {
      console.error("Failed to caching chat historical payload: ", e);
    }
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, isLoading]);

  const handleSendMessage = async (textToSend: string) => {
    if (!textToSend.trim() || isLoading) return;

    const userMsg: ChatMessage = {
      id: Math.random().toString(),
      role: 'user',
      text: textToSend,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    const updated = [...messages, userMsg];
    saveHistory(updated);
    setInput('');
    setIsLoading(true);

    try {
      // Create list format mapping to role: user/model with text params
      const chatHistory = updated.slice(-10).map(m => ({
        role: m.role,
        text: m.text
      }));

      const res = await fetch('/api/agent/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: textToSend,
          history: chatHistory.slice(0, -1) // All items except the newly pushed one
        })
      });

      if (!res.ok) {
        throw new Error(`Server returned error status: ${res.status}`);
      }

      const data = await res.json();
      
      const botMsg: ChatMessage = {
        id: Math.random().toString(),
        role: 'model',
        text: data.text,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      saveHistory([...updated, botMsg]);
    } catch (error) {
      console.error("Failed to exchange chat text payload: ", error);
      const errorMsg: ChatMessage = {
        id: Math.random().toString(),
        role: 'model',
        text: t('aiBotConnectionError'),
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      saveHistory([...updated, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearHistory = () => {
    if (window.confirm(t('aiBotClearConfirm'))) {
      const restarted: ChatMessage = {
        id: 'welcome',
        role: 'model',
        text: t('aiBotWelcomeClean'),
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      saveHistory([restarted]);
    }
  };

  // Keep track of unread messages when minimized
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    } else if (messages.length > 0) {
      const last = messages[messages.length - 1];
      if (last.role === 'model' && last.id !== 'welcome') {
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [messages, isOpen]);

  return (
    <div className="fixed bottom-6 right-6 z-[9999] select-none font-sans">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="absolute bottom-16 right-0 bg-[#0f172a] border border-slate-800 rounded-2xl shadow-2xl flex flex-col sm:w-[380px] w-[calc(100vw-32px)] h-[500px] max-h-[calc(100vh-100px)] overflow-hidden"
          >
            {/* Bot Header Widget */}
            <div className="p-4 bg-gradient-to-r from-blue-950 to-indigo-950 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center text-white relative">
                  <Brain size={16} className="text-blue-100 animate-pulse" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-[#0f172a]" />
                </div>
                <div>
                  <h3 className="text-xs font-black text-white leading-tight uppercase tracking-wider font-mono">{t('aiBotTitle')}</h3>
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full inline-block animate-ping" />
                    <span className="text-[9px] font-mono text-slate-400 uppercase tracking-tighter">{t('officialCollectibleDetails')}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleClearHistory}
                  title="Clear conversation history"
                  className="p-1.5 text-slate-500 hover:text-red-400 rounded-lg transition-colors cursor-pointer"
                >
                  <Trash2 size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* Chat Messages Body */}
            <div className="flex-1 p-4 overflow-y-auto space-y-4 min-h-0 bg-[#090d16]">
              {messages.map((msg) => (
                <div 
                  key={msg.id}
                  className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
                >
                  <div 
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-xs shadow-md ${
                      msg.role === 'user' 
                        ? 'bg-blue-600 text-white rounded-tr-none' 
                        : 'bg-[#1e293b] border border-slate-800/80 rounded-tl-none text-slate-150'
                    }`}
                  >
                    <FormatBotResponse text={msg.text} />
                  </div>
                  <span className="text-[8px] font-mono text-slate-500 mt-1 uppercase tracking-widest px-1">
                    {msg.timestamp}
                  </span>
                </div>
              ))}

              {isLoading && (
                <div className="flex flex-col items-start">
                  <div className="bg-[#1e293b] border border-slate-800/80 px-4 py-3 rounded-2xl rounded-tl-none flex items-center gap-2 text-xs text-slate-400">
                    <Sparkles size={12} className="text-blue-400 animate-spin" />
                    <div className="flex gap-1 items-center font-mono text-[10px] text-slate-300">
                      <span>{t('aiBotAnalyzing')}</span>
                      <span className="animate-bounce">.</span>
                      <span className="animate-bounce delay-100">.</span>
                      <span className="animate-bounce delay-200">.</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Prompt Preset Chips Overlay */}
            <div className="px-3 py-2 bg-[#0d1422] border-t border-slate-900 overflow-x-auto whitespace-nowrap flex gap-2 scrollbar-none">
              {PRESET_CHIPS.map((chip, ip) => (
                <button
                  key={ip}
                  type="button"
                  disabled={isLoading}
                  onClick={() => handleSendMessage(chip.text)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-900 border border-slate-800 hover:border-slate-700 text-slate-300 rounded-full text-[10px] tracking-tight hover:text-white transition-all cursor-pointer whitespace-nowrap shrink-0 disabled:opacity-50"
                >
                  {t(chip.label) || chip.label}
                </button>
              ))}
            </div>

            {/* Input Bar Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage(input);
              }}
              className="p-3 bg-slate-950 border-t border-slate-900 flex items-center gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                maxLength={400}
                placeholder={t('aiBotPlaceholder')}
                className="flex-1 bg-slate-905 bg-[#0b101b] border border-slate-800 text-white text-xs px-3.5 py-2.5 rounded-xl placeholder-slate-500 focus:outline-none focus:border-blue-600"
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-600 text-white p-2.5 rounded-xl shadow-lg transition-colors cursor-pointer shrink-0"
              >
                <Send size={14} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Messenger Icon Button */}
      <motion.button
        type="button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-xl cursor-pointer hover:bg-blue-500 transition-colors group border-2 border-slate-900 bg-gradient-to-br from-blue-500 to-indigo-600"
      >
        <MessageSquare size={18} className="group-hover:rotate-6 transition-transform" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-mono font-black font-extrabold w-5 h-5 flex items-center justify-center rounded-full border border-slate-900 shadow-md animate-bounce">
            {unreadCount}
          </span>
        )}
        <span className="absolute right-14 bg-slate-950/90 text-slate-200 text-[10px] font-bold font-mono px-2.5 py-1 rounded-md shadow border border-slate-850 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap hidden sm:inline leading-none uppercase tracking-wide">
          🔑 {t('aiBotUnread')}
        </span>
      </motion.button>
    </div>
  );
}
